import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiUser, FiCalendar, FiAward, FiUsers, FiX, FiHash, FiFileText, FiGift, FiMessageCircle, FiBook, FiSearch } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { agentAPI } from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { AGENT_STATUS_OPTIONS, getAgentStatusMeta } from '../../utils/agentStatus';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('th-TH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const DownlineModal = ({ isOpen, onClose, agent }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  if (!isOpen) return null;

  // Use real downlines from agent object
  const downlines = agent?.downlines || [];
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredDownlines = downlines.filter((downline) => {
    const status = getAgentStatusMeta(downline.agentStatus).status;
    const searchable = `${downline.firstName || ''} ${downline.lastName || ''} ${downline.nickname || ''} ${downline.agentCode || ''}`.toLowerCase();
    return (!normalizedSearch || searchable.includes(normalizedSearch)) &&
      (filterStatus === 'all' || status === filterStatus);
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

        <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-10 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6 h-[80vh] flex flex-col">
          <div className="absolute top-0 right-0 pt-4 pr-4 z-10">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={onClose}
            >
              <span className="sr-only">ปิด</span>
              <SafeIcon icon={FiX} className="h-6 w-6" />
            </button>
          </div>
          
          <div className="sm:flex sm:items-start w-full h-full flex-col">
            <div className="mt-3 text-center sm:mt-0 sm:ml-0 sm:text-left w-full h-full flex flex-col">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex-shrink-0" id="modal-title">
                ลูกทีมของ {agent?.firstName} {agent?.lastName}
              </h3>
              <div className="mt-4 flex-1 flex flex-col min-h-0">
                <p className="text-sm text-gray-500 mb-4 flex-shrink-0">
                  แสดง {filteredDownlines.length} จาก {downlines.length} คน
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-shrink-0">
                  <div className="relative">
                    <SafeIcon icon={FiSearch} className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="ค้นหาชื่อหรือรหัสตัวแทน" className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:ring-indigo-500" />
                  </div>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500">
                    <option value="all">สถานะทั้งหมด</option>
                    {AGENT_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
                <div className="flow-root mt-4 overflow-y-auto flex-1 pr-2">
                  {filteredDownlines.length > 0 ? (
                    <ul className="divide-y divide-gray-200 pt-2">
                      {filteredDownlines.map((downline) => {
                        const statusMeta = getAgentStatusMeta(downline.agentStatus);
                        return (
                        <li key={downline.id} className={`py-4 hover:bg-gray-50 rounded-lg transition-colors px-3 mt-2 first:mt-0 border-l-4 ${statusMeta.border}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="flex-shrink-0">
                                 <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center border border-indigo-200">
                                    <SafeIcon icon={FiUser} className="h-5 w-5 text-indigo-600" />
                                 </div>
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {downline.firstName} {downline.lastName}
                                </p>
                                <div className="flex items-center text-xs text-gray-500 mt-1 space-x-2">
                                  <span className="bg-gray-100 px-1.5 py-0.5 rounded">{downline.agentCode}</span>
                                  <span className="text-gray-300">|</span>
                                  <span>{downline.position || 'ตัวแทน'}</span>
                                  <span className="text-gray-300">|</span>
                                  <span>เข้าร่วม: {formatDate(downline.joinDate)}</span>
                                </div>
                                <span className={`inline-flex mt-2 px-2 py-0.5 rounded-full text-[10px] font-medium ${statusMeta.badge}`}>{statusMeta.label}</span>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-6 text-xs text-gray-500">
                               <div className="flex flex-col items-center min-w-[50px]" title="จำนวนกรมธรรม์">
                                  <div className="flex items-center mb-1">
                                    <SafeIcon icon={FiFileText} className="w-4 h-4 text-green-500 mr-1"/>
                                    <span className="font-semibold text-gray-700">{downline.policies?.length || 0}</span>
                                  </div>
                                  <span className="text-[10px] text-gray-400">กรมธรรม์</span>
                               </div>
                               <div className="flex flex-col items-center min-w-[50px]" title="จำนวนการโค้ช">
                                  <div className="flex items-center mb-1">
                                    <SafeIcon icon={FiMessageCircle} className="w-4 h-4 text-orange-500 mr-1"/>
                                    <span className="font-semibold text-gray-700">{downline.coachingLogs?.length || 0}</span>
                                  </div>
                                  <span className="text-[10px] text-gray-400">การโค้ช</span>
                               </div>
                               <div className="flex flex-col items-center min-w-[50px]" title="จำนวนการอบรม">
                                  <div className="flex items-center mb-1">
                                    <SafeIcon icon={FiBook} className="w-4 h-4 text-blue-500 mr-1"/>
                                    <span className="font-semibold text-gray-700">{downline.trainings?.length || 0}</span>
                                  </div>
                                  <span className="text-[10px] text-gray-400">การอบรม</span>
                               </div>
                            </div>
                          </div>
                        </li>
                      )})}
                    </ul>
                  ) : (
                    <div className="text-center py-12 text-gray-400 border-2 border-dashed border-gray-100 rounded-lg flex flex-col items-center justify-center h-full">
                       <SafeIcon icon={FiUsers} className="h-12 w-12 mb-3 text-gray-300" />
                       <p>ไม่มีลูกทีมในสังกัด</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AgentCard = ({ agent, onClick, onStatusChange, updating }) => {
  const imageUrl = agent.user?.pictureUrl;
  const fullName = `${agent.firstName} ${agent.lastName}`;
  const agentPolicyCount = agent.policies?.length || 0;
  const downlinePolicyCount = agent.downlines?.reduce((sum, d) => sum + (d.policies?.length || 0), 0) || 0;
  const statusMeta = getAgentStatusMeta(agent.agentStatus);

  return (
    <div 
      onClick={() => onClick(agent)}
      className={`bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow duration-300 cursor-pointer border border-gray-100 border-l-4 ${statusMeta.border}`}
    >
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0 h-16 w-16">
            {imageUrl ? (
              <img className="h-16 w-16 rounded-full object-cover border-2 border-indigo-100" src={imageUrl} alt={fullName} />
            ) : (
              <div className="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center border-2 border-indigo-50">
                <SafeIcon icon={FiUser} className="h-8 w-8 text-indigo-500" />
              </div>
            )}
          </div>
          <div className="ml-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">{fullName}</h3>
            <p className="text-sm text-indigo-600 font-medium">{agent.position || 'ตัวแทน'}</p>
             <div className="flex items-center text-xs text-gray-500 mt-1">
              <SafeIcon icon={FiHash} className="mr-1 h-3 w-3" />
              {agent.agentCode || '-'}
            </div>
          </div>
        </div>

        <div className="mb-4" onClick={(e) => e.stopPropagation()}>
          <label className="block text-xs font-medium text-gray-500 mb-1">สถานภาพตัวแทน</label>
          <select
            value={statusMeta.status}
            disabled={updating}
            onChange={(e) => onStatusChange(agent, e.target.value)}
            className={`w-full rounded-lg border-0 px-3 py-2 text-xs font-medium focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 ${statusMeta.badge}`}
          >
            {AGENT_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
        
        <div className="border-t border-gray-100 pt-4 space-y-2">
           <div className="flex justify-between items-center text-sm">
            <div className="flex items-center text-gray-500">
              <SafeIcon icon={FiCalendar} className="mr-2 h-4 w-4 text-gray-400" />
              <span>วันที่เข้าร่วม</span>
            </div>
            <span className="font-medium text-gray-900">{formatDate(agent.joinDate)}</span>
          </div>

          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center text-gray-500">
              <SafeIcon icon={FiGift} className="mr-2 h-4 w-4 text-gray-400" />
              <span>วันเกิด</span>
            </div>
            <span className="font-medium text-gray-900">{formatDate(agent.birthDate)}</span>
          </div>

          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center text-gray-500">
              <SafeIcon icon={FiAward} className="mr-2 h-4 w-4 text-gray-400" />
              <span>ใบอนุญาต</span>
            </div>
            <div className="text-right">
                <span className="block font-medium text-gray-900">{agent.licenseLevel || '-'}</span>
                <span className="text-xs text-gray-400">
                    {formatDate(agent.licenseStartDate)} - {formatDate(agent.licenseExpiryDate)}
                </span>
            </div>
          </div>

          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center text-gray-500">
              <SafeIcon icon={FiUsers} className="mr-2 h-4 w-4 text-gray-400" />
              <span>ลูกทีม</span>
            </div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              {agent.downlines?.length || 0}
            </span>
          </div>

          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center text-gray-500">
              <SafeIcon icon={FiFileText} className="mr-2 h-4 w-4 text-gray-400" />
              <span>กรมธรรม์ส่วนตัว</span>
            </div>
            <span className="font-medium text-gray-900">{agentPolicyCount}</span>
          </div>

          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center text-gray-500">
              <SafeIcon icon={FiFileText} className="mr-2 h-4 w-4 text-gray-400" />
              <span>กรมธรรม์ทีม</span>
            </div>
            <span className="font-medium text-gray-900">{downlinePolicyCount}</span>
          </div>

        </div>
      </div>
    </div>
  );
};

const AdminAgents = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [updatingAgentId, setUpdatingAgentId] = useState(null);

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await agentAPI.getAgents();
      // Filter out System Manager / System Administrator and ensure agent has a user account
      const filteredAgents = data.filter(agent => {
        // Must have a linked user account to be considered an "Active Agent" usually, 
        // but AdminAgents might show all agents? The original logic filtered.
        // Let's stick to showing agents with user accounts as "Agents", and others are "Clients/Downlines" (in AdminClients).
        if (!agent.user) return false;

        const name = `${agent.firstName} ${agent.lastName}`;
        const userDisplayName = agent.user?.displayName;
        const forbiddenNames = ['System Manager', 'System Administrator'];
        
        if (forbiddenNames.includes(name)) return false;
        if (userDisplayName && forbiddenNames.includes(userDisplayName)) return false;
        
        return true;
      });
      setAgents(filteredAgents);
    } catch (error) {
      toast.error('ไม่สามารถดึงข้อมูลตัวแทนได้');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleCardClick = async (agent) => {
    // Fetch full details to ensure downlines are populated
    try {
        const { data } = await agentAPI.getAgent(agent.id);
        setSelectedAgent(data);
        setIsModalOpen(true);
    } catch (error) {
        console.error("Failed to fetch agent details", error);
        setSelectedAgent(agent); // Fallback to list data
        setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAgent(null);
  };

  const handleStatusChange = async (agent, agentStatus) => {
    setUpdatingAgentId(agent.id);
    try {
      const { data } = await agentAPI.updateAgent(agent.id, { agentStatus });
      setAgents((current) => current.map((item) => item.id === agent.id ? { ...item, agentStatus: data.agentStatus } : item));
      toast.success('อัปเดตสถานภาพตัวแทนแล้ว');
    } catch (error) {
      toast.error(error.response?.data?.message || 'ไม่สามารถอัปเดตสถานภาพได้');
    } finally {
      setUpdatingAgentId(null);
    }
  };

  const filteredAgents = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return agents.filter((agent) => {
      const searchable = `${agent.firstName || ''} ${agent.lastName || ''} ${agent.nickname || ''} ${agent.agentCode || ''}`.toLowerCase();
      const status = getAgentStatusMeta(agent.agentStatus).status;
      return (!normalizedSearch || searchable.includes(normalizedSearch)) &&
        (filterStatus === 'all' || status === filterStatus);
    });
  }, [agents, searchTerm, filterStatus]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-6 min-h-[calc(100vh-100px)] flex flex-col">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">จัดการตัวแทน</h1>
        <p className="text-gray-600">ภาพรวมข้อมูลตัวแทนและโครงสร้างทีม (แสดง {filteredAgents.length} จาก {agents.length} คน)</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="ค้นหาชื่อ ชื่อเล่น หรือรหัสตัวแทน" className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:ring-indigo-500" />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500">
          <option value="all">สถานะทั้งหมด</option>
          {AGENT_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex-1 w-full">
           <LoadingSpinner />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {filteredAgents.map((agent) => (
            <AgentCard 
              key={agent.id} 
              agent={agent} 
              onClick={handleCardClick} 
              onStatusChange={handleStatusChange}
              updating={updatingAgentId === agent.id}
            />
          ))}
          {filteredAgents.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              ไม่พบข้อมูลตัวแทน
            </div>
          )}
        </div>
      )}

      <DownlineModal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
        agent={selectedAgent} 
      />
    </div>
  );
};

export default AdminAgents;
